import { useTransition } from "react";
import { useQueryLoader } from "react-relay";

import { useDeepCompareEffect, useSyncedRef } from "@skydio/rivet-ui";

import type { UseQueryLoaderLoadQueryOptions } from "react-relay";
import type { GraphQLTaggedNode, OperationType, VariablesOf } from "relay-runtime";

export type UseLoadQueryOptions = UseQueryLoaderLoadQueryOptions & {
  suspendOnRefetch?: boolean;
};

export const useLoadQuery = <TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  variables: VariablesOf<TQuery>,
  options?: UseLoadQueryOptions
) => {
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<TQuery>(query);

  const [isLoading, startTransition] = useTransition();

  const optionsRef = useSyncedRef(options);

  useDeepCompareEffect(() => {
    if (optionsRef.current?.suspendOnRefetch) {
      loadQuery(variables);
    } else {
      startTransition(() => {
        loadQuery(variables, optionsRef.current);
      });
    }
    return () => {
      disposeQuery();
    };
  }, [variables]);

  return [queryReference, isLoading, disposeQuery] as const;
};
